import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/UnifiedAuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Skeleton } from '../components/ui';

// Icon components
const Icons = {
  Success: () => (
    <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
    </svg>
  ),
  Error: () => (
    <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
    </svg>
  )
};

const MagicLinkVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const hasVerified = useRef(false); // Prevent multiple verification attempts

  const verify = useCallback(async (token, email) => {
    if (hasVerified.current) {
      return;
    }
    
    hasVerified.current = true;
    
    const result = await verifyMagicLink(token, email);
    
    if (result.success) {
      setStatus('success');
      setMessage('Successfully logged in! Redirecting...');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } else {
      setStatus('error');
      setMessage(result.error || 'Failed to verify magic link');
    }
  }, [verifyMagicLink, navigate]);

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (!token || !email) {
      setStatus('error');
      setMessage('Invalid magic link. Missing token or email.');
      return;
    }

    verify(token, email);
  }, [searchParams, verify]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-navy-900">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-navy-900 dark:border dark:border-gray-800 rounded-lg shadow-md p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="space-y-3">
                <Skeleton className="h-12 w-12 rounded-full mx-auto" />
                <Skeleton className="h-6 w-48 mx-auto" />
                <Skeleton className="h-4 w-64 mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Magic Link</h2>
              <p className="text-gray-600">Please wait while we log you in...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4 text-green-600">
                <Icons.Success />
              </div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">Login Successful!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <div className="animate-pulse">
                <div className="h-2 bg-green-200 rounded-full">
                  <div className="h-2 bg-green-600 rounded-full transition-all duration-2000" style={{width: '100%'}}></div>
                </div>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4 text-red-600">
                <Icons.Error />
              </div>
              <h2 className="text-2xl font-bold text-red-600 mb-2">Login Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Home
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MagicLinkVerification;
